import { type CSSProperties } from 'react';
import { useLoadingStore, type LoaderSymbolKey as Sym } from './loadingStore';

interface Props {
  /** Force a specific symbol (e.g. the App Config preview). Omit to use the
   *  client-configured symbol from the store — the normal app-wide behaviour. */
  symbol?: Sym;
  /** Pixel size of the symbol (default 28). */
  size?: number;
}

/**
 * Renders the active loading symbol.  With no `symbol` prop it reads the
 * client-configured symbol from the global store, so every loader (grid,
 * overlay, scrim) renders the SAME symbol the tenant chose in App Config.
 * Colour comes from the `--ai-loader-color` theme token (loading.css).
 */
export function LoaderSymbol({ symbol, size = 28 }: Props) {
  const configured = useLoadingStore((s) => s.symbol);
  const sym: Sym = symbol ?? configured;
  const style = { '--ld-size': `${size}px` } as CSSProperties;
  const common = { className: '', style, role: 'status' as const, 'aria-label': 'Loading' };

  switch (sym) {
    case 'dots':
      return <span {...common} className="ld ld-dots"><span /><span /><span /></span>;
    case 'bars':
      return <span {...common} className="ld ld-bars"><span /><span /><span /><span /><span /></span>;
    case 'pulse':
      return <span {...common} className="ld"><span className="ld-pulse" /></span>;
    case 'dual-ring':
      return <span {...common} className="ld"><span className="ld-dual" /></span>;
    case 'orbit':
      return <span {...common} className="ld"><span className="ld-orbit" /></span>;
    case 'ring':
    default:
      return <span {...common} className="ld"><span className="ld-ring" /></span>;
  }
}
